import { clsx } from 'clsx'
import { Modal, Heading, Button, Alert } from '~/components'
import { HireExtras } from '~/components/partials/product'
import { getCurrencySymbol, formatPrice } from '~/utils'
import { usePurchaseModal } from '~/hooks'
import { useRouteLoaderData } from '@remix-run/react'
import { useProductSingleContext } from '~/context/productSingle'

import type { LunarProductPreBasket, RootDataProps } from '~/types'

interface Props {
  open: boolean
  setOpen: (e: boolean) => void
  addToBasket: (e: LunarProductPreBasket[]) => void
  onClose: () => void
}

/**
 * Purchase modal — shows the extras (add-ons + cross-sells) the user
 * can attach to their basket line before committing. Reads the
 * underlying charges result, product data and fulfilment state from
 * `useProductSingleContext()` so the only props are modal lifecycle
 * and the basket-confirm callback.
 */
const PurchaseModal = ({ open, setOpen, addToBasket, onClose }: Props) => {
  const { user } = useRouteLoaderData('root') as RootDataProps
  const {
    product,
    category,
    charges,
    fulfilment,
    basketLoading,
    basketError,
    basketProduct,
  } = useProductSingleContext()

  const {
    totalStages,
    currentStage,
    grossTotal,
    vatTotal,
    hireExVat,
    purchaseExVat,
    extrasBasket,
    basket,
    quantityError,
    addOns,
    crossSells,
    header,
    setCurrentStage,
    setAddOns,
    setCrossSells,
    handleClose,
  } = usePurchaseModal({
    open,
    charges,
    category,
    fulfilment,
    parentQuantity: basketProduct?.quantity ?? 1,
    productName: product.name,
    productType: product.hyraxProductType,
    loggedIn: Boolean(user),
    onClose,
  })

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      size="2xl"
      fullHeightMobile
    >
      <div className="mb-6">
        <Heading
          tag="h3"
          size="h4"
          className="mb-4 flex-1 px-4 text-center"
        >
          {header.title}
        </Heading>
        <p className="mb-0 text-center">{header.description}</p>
      </div>

      {totalStages !== 1 && (
        <div className="relative mx-auto mb-8 h-2 w-48 bg-neutral-200 md:mb-10 md:w-96">
          <div
            className={clsx(
              'absolute left-0 top-0 z-[2] h-2 bg-teal-green transition-all duration-500',
              {
                'w-[50%]': currentStage == 1,
                'w-[100%]': currentStage == 2,
              }
            )}
          >
            <div className="absolute -right-1 top-1/2 z-[3] size-4 -translate-y-1/2 rounded-full bg-teal-green">
              <div className="absolute left-1/2 top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
            </div>
          </div>
          <div className="absolute -right-1 top-1/2 z-[1] size-4 -translate-y-1/2 rounded-full bg-neutral-200" />
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-10">
        <div className="md:col-span-8 xl:col-span-9">
          <HireExtras
            addOns={addOns}
            crossSells={crossSells}
            totalStages={totalStages}
            currentStage={currentStage}
            setAddOns={setAddOns}
            setCrossSells={setCrossSells}
          />
        </div>

        <div className="flex flex-col md:col-span-4 xl:col-span-3">
          <div className="mb-6 rounded-xl bg-neutral-150 p-6">
            <Heading
              tag="h3"
              size="h6"
            >
              Extras summary
            </Heading>

            {extrasBasket.length > 0 ? (
              <>
                <div className="mb-2 hidden justify-between text-sm md:flex">
                  <div className="flex-1 font-semibold">Name</div>
                  <div className="font-semibold">Quantity</div>
                </div>

                <div className="mb-4 hidden max-h-64 overflow-y-auto md:block">
                  {extrasBasket.map(e => (
                    <div
                      key={e.stockCode}
                      className="flex justify-between text-sm"
                    >
                      <div className="flex-1 border-b border-neutral-300 py-2">
                        {e.title}
                      </div>
                      <div className="border-b border-neutral-300 px-2 py-2 text-right">
                        {e.quantity}
                      </div>
                    </div>
                  ))}
                </div>

                {hireExVat > 0 && (
                  <div className="flex justify-between text-sm">
                    <div className="flex-1">Items for hire:</div>
                    <div>
                      {getCurrencySymbol('GBP') + formatPrice(hireExVat)}
                    </div>
                  </div>
                )}
                {purchaseExVat > 0 && (
                  <div className="mt-1 flex justify-between text-sm">
                    <div className="flex-1">Items for purchase:</div>
                    <div>
                      {getCurrencySymbol('GBP') + formatPrice(purchaseExVat)}
                    </div>
                  </div>
                )}
                <div className="mt-1 flex justify-between text-sm">
                  <div className="flex-1">VAT:</div>
                  <div>{getCurrencySymbol('GBP') + formatPrice(vatTotal)}</div>
                </div>
                <div className="mt-1 flex justify-between text-sm">
                  <div className="flex-1 font-semibold">Subtotal:</div>
                  <div className="font-semibold">
                    {getCurrencySymbol('GBP') + formatPrice(grossTotal)}
                  </div>
                </div>
              </>
            ) : (
              <p className="mb-0">No extras selected</p>
            )}
          </div>

          <div className="mt-auto">
            {quantityError && (
              <div className="mb-4">
                <Alert
                  message="Please check the quantities of the extras you have selected"
                  icon="exclamation"
                  type="error"
                  size="sm"
                />
              </div>
            )}

            {currentStage === totalStages ? (
              <>
                {totalStages !== 1 && (
                  <div className="mb-3">
                    <Button
                      title="Back"
                      onClick={() => setCurrentStage(1)}
                      style="tealGreen"
                      fullWidth
                    />
                  </div>
                )}
                {basketError && (
                  <p className="mb-3 text-sm text-red-600">{basketError}</p>
                )}
                <Button
                  title={basketLoading ? 'Loading..' : 'Continue to basket'}
                  onClick={() => addToBasket(basket)}
                  disabled={basketLoading || quantityError}
                  fullWidth
                />
              </>
            ) : (
              <div className="mb-3">
                <Button
                  title="Continue to basket"
                  disabled={quantityError}
                  onClick={() => setCurrentStage(2)}
                  fullWidth
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default PurchaseModal
